/**
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */

import {ColDef} from 'ag-grid-community';
import isEqual from 'lodash.isequal';
import {sortColumns} from '@ion/applications/src/store/helpers';
import {GenericObject} from '@ion/core/src/types/entities';
import {safeJSONParse, safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {
  ColumnFormatKeys,
  ColumnFormatType,
  ColumnWithOrderKey,
  GetSavedColAndCurrentColReturnType,
  SavedColumnItem
} from './types';

interface FilterColumnsParams {
  columns: ColDef[];
  doRemoveCellRendererCallback?: (column: ColDef) => boolean;
  excludedColumns?: string[];
  filterableColumns?: string[] | null;
  isSortByAlphaNumeric?: boolean;
}

/**
 * Utility function returns new Column with the order property.
 * @param columns is a type of ColDef[].
 * @returns ColumnWithOrderKey[] type.
 */
export const getColumnWithOrderKey = (columns: ColDef[]): ColumnWithOrderKey[] => {
  const result = columns?.map((column, index) => ({
    ...column,
    order : index
  }));
  return result || [];
};

/**
 * Utility function that return selected Columns in a required format.
 * @param columns is a type of ColumnWithOrderKey[][].
 * @returns will return a column in required format.
 */
export const getColumnInRequiredFormat = (columns: ColumnWithOrderKey[]): GenericObject[] => {
  const result = columns?.map(column => ({
    [ColumnFormatKeys.COL_ID] : column.field,
    [ColumnFormatKeys.ORDER]  : column.order,
    [ColumnFormatKeys.SORT]   : column.sort,
    [ColumnFormatKeys.WIDTH]  : column.width
  }));
  return result || [];
};

/**
 * Utility function checks whether the new name already exists in the saved column.
 * @param views This is a collection of saved column items.
 * @param name This is the name with which we want to save the new column.
 * @returns Will return a boolean true value if the name already exists in the saved column,
 * otherwise it will return a boolean false.
 */
export const checkIsNameExist = (views: SavedColumnItem[], name: string): boolean =>
  views?.some((view) => view?.viewName?.toLocaleLowerCase() === name?.toLocaleLowerCase());

/**
 * Utility function returns updated Views array with new column value
 * according to a given viewName.
 */
export const updateViewColumn = (
  views: SavedColumnItem[],
  viewName: string,
  newColumn: string
): SavedColumnItem[] => {
  return views?.map(view => {
    if (view?.viewName === viewName) {
      return {...view, columns: newColumn};
    }
    return view;
  }) || [];
};

/**
 * Get Column without  the Width property ...
 */
export const getColumnWithoutWidth = (columnFormat: ColumnFormatType[]): ColumnFormatType[] => (
  Array.isArray(columnFormat) ? columnFormat.map(({width, ...rest}) => rest) : []
);

/**
 * Get a new columns with field name value only ...
 */
export const getSelectedColumnFieldName = (columns: ColDef[]) => {
  return columns?.filter((column: ColDef) => (
    column?.hide === false
  ))?.map((column: ColDef) => ({
    field : column.field
  })) || [];
};

/**
 * Utility function returns a boolean value if it is a Select All columns ...
 */
export const checkIsSelectAll = (columns: ColDef[]): boolean =>
  columns?.length ? columns?.every((column) => !column?.hide) : false;

/**
 * Get selected saved column View's column ...
 */
export const getSelectedSavedViewColumn = (
  savedColumnViews: SavedColumnItem[],
  selectedColumnName: string
) => {
  if (savedColumnViews?.length) {
    const selectedSavedView = savedColumnViews?.find(
      ({viewName}) => viewName === selectedColumnName
    );
    return selectedSavedView?.columns || '';
  }
  return '';
};

/**
 * Get selected saved column view's column and current checked columns to compare ...
 */
export const getSavedColumnAndCurrentColumn = (
  savedColumnViews: SavedColumnItem[],
  selectedColumnName: string,
  selectedColumns: ColumnWithOrderKey[]
): GetSavedColAndCurrentColReturnType => {
  const selectedViewColumn             = getSelectedSavedViewColumn(savedColumnViews, selectedColumnName);
  const currentColumnInRequiredFormat  = getColumnInRequiredFormat(selectedColumns);
  const currentColumnWithoutWidth      = getColumnWithoutWidth(currentColumnInRequiredFormat);
  const selectedViewColumnWithoutWidth = getColumnWithoutWidth(safeJSONParse(selectedViewColumn));
  const selectedSavedViewColumn        = safeJSONStringify(selectedViewColumnWithoutWidth);
  const currentColumn                  = safeJSONStringify(currentColumnWithoutWidth);
  const currentColumnWithWidth         = safeJSONStringify(currentColumnInRequiredFormat);
  return {
    selectedSavedViewColumn,
    currentColumn,
    currentColumnWithWidth
  };
};

/**
 * Get current column view when re-login or at first render ...
 */
export const getCurrentColumnView = (
  savedColumnViews: SavedColumnItem[],
  currentColumn: string
): SavedColumnItem => (
  savedColumnViews?.find(item => {
    const viewsColumnWithoutWidth = getColumnWithoutWidth(safeJSONParse(item?.columns));
    const savedColumnViewsColumn = safeJSONStringify(viewsColumnWithoutWidth);
    return savedColumnViewsColumn === currentColumn;
  })
);

/**
 * Check if current view is default type or select all type view ...
 */
export const checkIsDefaultOrSelectAllView = (
  defaults: ColDef[],
  columns: ColDef[],
  selectedColumns: ColumnWithOrderKey[]
): boolean => {
  const defaultColumnsFiledsName = getSelectedColumnFieldName(defaults);
  const selectedColumnFieldsName = getSelectedColumnFieldName(selectedColumns);
  const isDefaultView            = isEqual(defaultColumnsFiledsName, selectedColumnFieldsName);
  const isSelectAllView          = checkIsSelectAll(columns);
  return isDefaultView || isSelectAllView;
};

/**
 * The `applyColumnOrdering` function provides a utility which accepts a list of
 * column definitions and optionally applies pre-defined ordering when the provided
 * `order` argument is set to `true`, in which case the columns are sorted
 * such that, visible columns remain in their original order, while hidden columns are
 * placed below the checked columns without being sorted by their respective header names.
 * 
 * By default, this function is pass-through (no-op) and returns the provided Column
 * Definitions as-is.
 *
 * ```typescript
 *   const columns = [{
 *     headerName: 'B', hide: true
 *   }, { 
 *     headerName: 'A', hide: false
 *   }, { 
 *     headerName: 'C', hide: true
 *   }];
 *
 *   const sortedColumns = applyColumnOrdering(columns, true);
 *   console.log(sortedColumns);
 *   // { headerName: 'A', hide: false }, 
 *   // { headerName: 'B', hide: true }, 
 *   // { headerName: 'C', hide: true }
 *
 *   const unsortedColumns = applyColumnOrdering(columns, false);
 *   console.log(unsortedColumns);
 *   // ... same as input
 * ```
 * 
 * {@link ColDef}
 * @param   columns The list of Column Definitions which are to be sorted.
 * @param   order A flag indicating whether sorting is to be provided or not, 
 *          defaults to `false` (no sorting by default).
 * @returns The sorted list of column definitions.
 */
export const applyColumnOrdering = (columns: ColDef[], order = false): ColDef[] => {
  if (order) {
    const visible: ColDef[] = [];
    const hidden: ColDef[]  = [];
    for (const column of columns) {
      (column.hide ? hidden : visible).push(column);
    }
    const name = (e: ColDef) => e?.headerName ?? '';
    hidden.sort((a, b) => name(a).localeCompare(name(b)));
    return [...visible, ...hidden];
  }
  return columns;
};

/**
 * Signals that a column is not filterable. Modifies the column in-place.
 * 
 * ```typescript
 * 
 *   import {markColumnAsNotFilterable} from '@ion/dls/src/grid/columns/utils.ts';
 * 
 *   // Note that the column is modified in-place
 *   markColumnAsNotFilterable(column);
 * 
 * ```
 */
export const markColumnAsNotFilterable = (column: ColDef): void => {
  column.filter = false;
  column.filterParams = undefined;
};

/**
 * Removes a column's cell-renderer properties. Modifies the column in-place.
 * 
 * ```typescript
 * 
 *   import {removeColumnCellRenderer} from '@ion/dls/src/grid/columns/utils.ts';
 * 
 *   // Note that the column is modified in-place
 *   removeColumnCellRenderer(column);
 * 
 * ```
 */
export const removeColumnCellRenderer = (column: ColDef): void => {
  delete column?.cellRenderer;
  delete column?.cellRendererParams;
};

/**
 * Processes columns based on given parameters.
 * Columns can be sorted, filtered, marked as filterable or not, 
 * and have their cell-renderer-related properties modified.
 * If any parameters' arguments are not specified, they default to having no effect.
 * 
 * ```typescript
 * 
 *   import {filterColumns} from '@ion/dls/src/grid/columns/utils.ts';
 *   import {ColDef} from 'ag-grid-community';
 * 
 *   // Only sorts the columns and nothing else
 *   const filteredColumns = filterColumns({columns, isSortByAlphaNumeric: true});
 *   
 *   // Excludes some columns
 *   const filteredColumns = filterColumns({columns, isSortByAlphaNumeric, excludedColumns: someColumnsToExclude});
 *   
 *   // Deletes some cell-renderer properties
 *   const filteredColumns = filterColumns({columns, isSortByAlphaNumeric, 
 *     doRemoveCellRendererCallback: (column) => (!enableAllHyperLinks && !enabledHyperLinks?.includes(column?.field)));
 * 
 * ```
 * 
 * @param param0 Object to be deconstructed. Contains properties that are the input arguments.
 * @param columns The columns to be processed.
 * @param doRemoveCellRendererCallback Callback that is passed each column and returns a boolean value 
 * signaling if the column's cell-renderer properties should be deleted.
 * @param excludedColumns Columns to not include in the result. Contains columns' `field` properties.
 * @param filterableColumns Columns to mark as not filterable. Contains columns' `field` properties.
 * @param isSortByAlphaNumeric Whether to sort the columns or not.
 * @returns The processed columns.
 */
export const filterColumns = ({
  columns,
  doRemoveCellRendererCallback = () => false,
  excludedColumns = [],
  filterableColumns = null,
  isSortByAlphaNumeric = false
}: FilterColumnsParams): ColDef[] => {
  if (isSortByAlphaNumeric) {
    columns = sortColumns(columns);
  }
  return columns.filter(column =>
    !excludedColumns.includes(column?.field as string)
  ).map(column => {
    if (filterableColumns !== null && !filterableColumns?.includes(column?.field as string)) {
      markColumnAsNotFilterable(column);
    }
    if (doRemoveCellRendererCallback(column)) {
      removeColumnCellRenderer(column);
    }
    return column;
  });
};
